import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Stack, IconButton, IconButtonProps } from '@mui/material';
import i18next from 'i18next';
import { isFunction } from 'lodash';
import { Link as RouterLink, LinkProps } from 'react-router-dom';

import { DataGridColumnDefinition } from '../types';

type Options<T> = {
  hidden?: boolean;
  handleEdit?: (item: T) => void;
  // Renders the edit button as a link, so ctrl/cmd/middle click and the context menu can open the page in a new tab
  getEditLink?: (item: T) => Pick<LinkProps, 'to' | 'state' | 'replace'>;
  editButtonProps?: IconButtonProps | ((item: T) => IconButtonProps);
  handleRemove?: (item: T) => void;
  removeButtonProps?: IconButtonProps | ((item: T) => IconButtonProps);
  children?: (item: T) => React.ReactNode | React.ReactNode[];
};

export function getActionsColumn<T>({
  hidden,
  handleEdit,
  getEditLink,
  handleRemove,
  children,
  editButtonProps,
  removeButtonProps,
}: Options<T>): DataGridColumnDefinition<T> {
  const { t } = i18next;
  if (!handleEdit && !getEditLink && !handleRemove && !children) {
    return null as unknown as DataGridColumnDefinition<T>;
  }
  return {
    header: t('columns.actions'),
    valueClassName: 'tw-w-[120px]',
    hidden,
    valueGetter: (item) => (
      <Stack direction="row" spacing={1}>
        {children?.(item)}
        {getEditLink && (
          <IconButton
            component={RouterLink}
            {...getEditLink(item)}
            color="primary"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            {...(isFunction(editButtonProps)
              ? editButtonProps(item)
              : editButtonProps)}
          >
            <EditIcon />
          </IconButton>
        )}
        {!getEditLink && handleEdit && (
          <IconButton
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(item);
            }}
            {...(isFunction(editButtonProps)
              ? editButtonProps(item)
              : editButtonProps)}
          >
            <EditIcon />
          </IconButton>
        )}
        {handleRemove && (
          <IconButton
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              handleRemove(item);
            }}
            {...(isFunction(removeButtonProps)
              ? removeButtonProps(item)
              : removeButtonProps)}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Stack>
    ),
  };
}
